import {combineReducers, configureStore} from "@reduxjs/toolkit";
import type {AnyAction, ThunkDispatch} from "@reduxjs/toolkit";

import {announcementsSlice} from "./bulletinBoard/announcementsSlice";
import {chapterSectionsSlice} from "./chapters/chapterSectionsSlice";
import {chaptersSlice} from "./chapters/chaptersSlice";
import {courseChaptersSlice} from "./chapters/courseChaptersSlice";
import {chapterExercisesSlice} from "./chapterExercises/chapterExercisesSlice";
import chapterSessionOutcomesSlice from "./chapterSessions/chapterSessionOutcomesSlice";
import chapterSessionsSlice from "./chapterSessions/chapterSessionsSlice";
import lastRequestedSessionSlice from "./chapterSessions/lastRequestedSessionSlice";
import sessionsSlice from "./chapterSessions/sessionsSlice";
import chapterSettingsOverridesSlice from "./chapterSettingsOverrides/overridesSlice";
import courseManagementModulesSlice from "./courses/courseManagementModulesSlice";
import {coursesSlice} from "./courses/coursesSlice";
import courseStudyModulesSlice from "./courses/courseStudyModulesSlice";
import courseProgressSlice from "./courseProgress/courseProgressSlice";
import chapterStatisticsSlice from "./courseStatistics/chapterStatisticsSlice";
import courseContentStatisticsSlice from "./courseStatistics/courseContentStatisticsSlice";
import courseStatisticsSlice from "./courseStatistics/courseStatisticsSlice";
import enrolmentApplicationsSlice from "./enrolment/enrolmentApplicationsSlice";
import {exerciseAnswersSlice} from "./exercises/exerciseAnswersSlice";
import exerciseDraftSlice from "./exercises/exericseDraftSlice";
import {exercisesSlice} from "./exercises/exercisesSlice";
import featuresSlice from "./features/featuresSlice";
import organisationFeaturesSlice from "./features/organisationFeaturesSlice";
import listener from "./listenerMiddleware";
import {organisationUserRolesSlice} from "./organisation/organisationUserRolesSlice";
import userOrganisationsSlice from "./organisation/userOrganisationsSlice";
import chapterOutcomesSlice from "./outcomes/chapterOutcomesSlice";
import {outcomesSlice} from "./outcomes/outcomesSlice";
import organisationsSlice from "./organisation/organisationsSlice";
import userOrganisationGroupsSlice from "./organisationGroups/userOrganisationGroupsSlice";
import proctoringSettingsSlice from "./proctoring/settingsSlice";
import {sectionsSlice} from "./sections/sectionsSlice";
import availableCoursesSlice from "./selfEnrolment/availableCoursesSlice";
import ownEnrolmentApplicationsSlice from "./selfEnrolment/enrolmentApplicationsSlice";
import {studentDashboardSlice} from "./studentDashboardSlice";
import {studentResponsesSlice} from "./studentResponses/studentResponsesSlice";
import studyPreferencesSlice from "./studyPreferences/studyPreferencesSlice";
import {teacherDashboardSlice} from "./teacherDashboardSlice";
import {themeSlice} from "./theme/themeSlice";
import tutorQuestionsSlice from "./tutorQuestions/tutorQuestionsSlice";
import chapterCompletionRewardSlice from "./ui/chapterCompletionRewardSlice";
import chapterEditingSlice from "./ui/chapterEditingSlice";
import courseEditingSlice from "./ui/courseEditingSlice";
import modalDialogSlice from "./ui/modalDialogSlice";
import notificationsSlice from "./ui/notificationsSlice";
import {userProfileSlice} from "./userProfile/userProfileSlice";
import userSessionSlice from "./userSession/userSessionSlice";
import availableCourseWidgetsSlice from "./widgets/availableCourseWidgetsSlice";
import courseWidgetKeysSlice from "./widgets/courseWidgetKeysSlice";
import courseWidgetSettingsSlice from "./widgets/courseWidgetSettingsSlice";

export const store = configureStore({
	reducer: {
		announcements: announcementsSlice.reducer,
		chapterExercises: chapterExercisesSlice.reducer,
		chapterSections: chapterSectionsSlice.reducer,
		chapterSessions: combineReducers<{
			chapterSessions: ReturnType<typeof chapterSessionsSlice>;
			sessions: ReturnType<typeof sessionsSlice>;
			lastRequested: ReturnType<typeof lastRequestedSessionSlice>;
			outcomes: ReturnType<typeof chapterSessionOutcomesSlice>;
		}>({
			chapterSessions: chapterSessionsSlice,
			sessions: sessionsSlice,
			lastRequested: lastRequestedSessionSlice,
			outcomes: chapterSessionOutcomesSlice,
		}),
		chapters: chaptersSlice.reducer,
		chapterOutcomes: chapterOutcomesSlice,
		chapterStatistics: chapterStatisticsSlice,
		chapterSettingsOverrides: chapterSettingsOverridesSlice,
		courseChapters: courseChaptersSlice.reducer,
		courseContentStatistics: courseContentStatisticsSlice,
		courses: coursesSlice.reducer,
		courseManagementModules: courseManagementModulesSlice,
		courseProgress: courseProgressSlice,
		courseStatistics: courseStatisticsSlice,
		courseStudyModules: courseStudyModulesSlice,
		enrolment: combineReducers<{
			applications: ReturnType<typeof enrolmentApplicationsSlice>;
		}>({
			applications: enrolmentApplicationsSlice,
		}),
		exerciseAnswers: exerciseAnswersSlice.reducer,
		exercises: exercisesSlice.reducer,
		features: combineReducers<{
			global: ReturnType<typeof featuresSlice>;
			organisation: ReturnType<typeof organisationFeaturesSlice>;
		}>({
			global: featuresSlice,
			organisation: organisationFeaturesSlice,
		}),
		organisations: organisationsSlice,
		organisationUserRoles: organisationUserRolesSlice.reducer,
		outcomes: outcomesSlice.reducer,
		proctoring: combineReducers<{
			settings: ReturnType<typeof proctoringSettingsSlice>;
		}>({settings: proctoringSettingsSlice}),
		sections: sectionsSlice.reducer,
		selfEnrolment: combineReducers<{
			applications: ReturnType<typeof ownEnrolmentApplicationsSlice>;
			availableCourses: ReturnType<typeof availableCoursesSlice>;
		}>({
			applications: ownEnrolmentApplicationsSlice,
			availableCourses: availableCoursesSlice,
		}),
		studentPreferences: studyPreferencesSlice,
		studentResponses: studentResponsesSlice.reducer,
		studentdashboard: studentDashboardSlice.reducer,
		teacherdashboard: teacherDashboardSlice.reducer,
		theme: themeSlice.reducer,
		tutorQuestions: tutorQuestionsSlice,
		ui: combineReducers<{
			chapterCompletionReward: ReturnType<typeof chapterCompletionRewardSlice>;
			chapterEditing: ReturnType<typeof chapterEditingSlice>;
			courseEditing: ReturnType<typeof courseEditingSlice>;
			exerciseDraft: ReturnType<typeof exerciseDraftSlice>;
			modalDialog: ReturnType<typeof modalDialogSlice>;
			notifications: ReturnType<typeof notificationsSlice>;
		}>({
			chapterCompletionReward: chapterCompletionRewardSlice,
			chapterEditing: chapterEditingSlice,
			courseEditing: courseEditingSlice,
			exerciseDraft: exerciseDraftSlice,
			modalDialog: modalDialogSlice,
			notifications: notificationsSlice,
		}),
		userOrganisationGroups: userOrganisationGroupsSlice,
		userOrganisations: userOrganisationsSlice,
		userProfile: userProfileSlice.reducer,
		userSession: userSessionSlice,
		widgets: combineReducers<{
			availableCourseWidgets: ReturnType<typeof availableCourseWidgetsSlice>;
			courseWidgetKeys: ReturnType<typeof courseWidgetKeysSlice>;
			courseWidgetSettings: ReturnType<typeof courseWidgetSettingsSlice>;
		}>({
			availableCourseWidgets: availableCourseWidgetsSlice,
			courseWidgetKeys: courseWidgetKeysSlice,
			courseWidgetSettings: courseWidgetSettingsSlice,
		}),
	},
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware().concat(listener.middleware),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = ThunkDispatch<RootState, unknown, AnyAction>;
